import {inject, injectable} from 'inversify'
import type {DomainEvent, EventHandler, EventSubscription, IEventBus,} from '@/src/core/domain/events'
import type {ILoggerService} from '@/src/ports/services/logger'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import {DI} from '@/src/config/di/types'

/**
 * In-Memory Event Bus Implementation
 *
 * Simple event bus that stores handlers in memory.
 * Suitable for single-instance applications.
 *
 * Features:
 * - Async event handling
 * - Error isolation (one handler failure doesn't affect others)
 * - Wildcard subscription (subscribeAll)
 * - Logging and error reporting
 */
@injectable()
export class InMemoryEventBus implements IEventBus {
  private handlers: Map<string, Set<EventHandler>> = new Map()
  private globalHandlers: Set<EventHandler> = new Set()

  @inject(DI.ILoggerService)
  private logger!: ILoggerService

  @inject(DI.IErrorReporter)
  private errorReporter!: IErrorReporter

  async publish<T extends DomainEvent>(event: T): Promise<void> {
    this.logger.debug('Publishing domain event', {
      eventType: event.eventType,
      eventId: event.eventId,
      aggregateId: event.aggregateId,
    })

    const handlers = this.handlers.get(event.eventType) ?? new Set()
    const allHandlers = [...handlers, ...this.globalHandlers]

    await Promise.allSettled(
      allHandlers.map(handler => this.safeExecuteHandler(handler, event)),
    )
  }

  async publishAll(events: DomainEvent[]): Promise<void> {
    for (const event of events) {
      await this.publish(event)
    }
  }

  subscribe<T extends DomainEvent>(
    eventType: string,
    handler: EventHandler<T>,
  ): EventSubscription {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set())
    }

    this.handlers.get(eventType)!.add(handler as EventHandler)

    this.logger.debug('Subscribed to event', {eventType})

    return {
      unsubscribe: () => {
        this.handlers.get(eventType)?.delete(handler as EventHandler)
        this.logger.debug('Unsubscribed from event', {eventType})
      },
    }
  }

  subscribeAll(handler: EventHandler): EventSubscription {
    this.globalHandlers.add(handler)

    this.logger.debug('Subscribed to all events')

    return {
      unsubscribe: () => {
        this.globalHandlers.delete(handler)
        this.logger.debug('Unsubscribed from all events')
      },
    }
  }

  clearHandlers(eventType: string): void {
    this.handlers.delete(eventType)
    this.logger.debug('Cleared handlers for event', {eventType})
  }

  clearAllHandlers(): void {
    this.handlers.clear()
    this.globalHandlers.clear()
    this.logger.debug('Cleared all event handlers')
  }

  private async safeExecuteHandler<T extends DomainEvent>(
    handler: EventHandler<T>,
    event: T,
  ): Promise<void> {
    try {
      await handler(event)
    } catch (error) {
      this.logger.error('Event handler failed', error, {
        eventType: event.eventType,
        eventId: event.eventId,
      })
      this.errorReporter.capture(error)
    }
  }
}
